import { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Search, Menu, X, Minus, Plus, Trash2, Instagram, Copy, Check, Home as HomeIcon } from 'lucide-react';
import LiveChat from './LiveChat';
import AdminPanel from './AdminPanel';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ================== Cart Context ==================
const CartContext = createContext();

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, size) => {
    const cartId = `${product.id}-${size}`;
    setCart(prev => {
      const existing = prev.find(item => item.cartId === cartId);
      if (existing) {
        return prev.map(item =>
          item.cartId === cartId ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, cartId, size, quantity: 1 }];
    });
  };

  const updateQuantity = (cartId, quantity) => {
    if (quantity <= 0) return removeFromCart(cartId);
    setCart(prev => prev.map(item => item.cartId === cartId ? { ...item, quantity } : item));
  };

  const removeFromCart = (cartId) => setCart(prev => prev.filter(item => item.cartId !== cartId));
  const clearCart = () => setCart([]);

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, updateQuantity, removeFromCart, clearCart, subtotal, cartCount }}>
      {children}
    </CartContext.Provider>
  );
};

const useCart = () => useContext(CartContext);

// ================== Settings Context ==================
const SettingsContext = createContext();

const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    axios.get(`${API}/settings`).then(res => setSettings(res.data));
  }, []);
  return <SettingsContext.Provider value={settings}>{children}</SettingsContext.Provider>;
};

const useSettings = () => useContext(SettingsContext);

// ================== Auth Context ==================
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem('auth_token') || '');

  const login = (userData, tok) => {
    setUser(userData);
    setToken(tok);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('auth_token', tok);
  };
  const logout = () => {
    setUser(null); setToken('');
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
  };
  return <AuthContext.Provider value={{ user, token, login, logout }}>{children}</AuthContext.Provider>;
};
const useAuth = () => useContext(AuthContext);

// ================== Auth Modal ==================
const AuthModal = ({ open, onClose }) => {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [err, setErr] = useState(''); const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault(); setErr(''); setLoading(true);
    try {
      const url = mode === 'login' ? `${API}/auth/login` : `${API}/auth/register`;
      const payload = mode === 'login' ? { email: form.email, password: form.password } : form;
      const res = await axios.post(url, payload);
      login(res.data.user, res.data.token);
      onClose();
    } catch (e) { setErr(e?.response?.data?.detail || 'Hata oluştu'); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="border-b px-5 py-3 flex items-center justify-between">
          <h3 className="font-serif font-bold text-lg text-[#2c1810]">
            {mode === 'login' ? 'Giriş Yap' : 'Üye Ol'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          {err && <div className="bg-red-50 text-red-700 border border-red-200 px-3 py-2 rounded text-sm">{err}</div>}
          {mode === 'register' && (
            <>
              <input required data-testid="auth-name" placeholder="Ad Soyad" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full border rounded px-3 py-2 text-sm focus:border-[#1a1a1a] focus:outline-none" />
              <input required data-testid="auth-phone" placeholder="Telefon" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full border rounded px-3 py-2 text-sm focus:border-[#1a1a1a] focus:outline-none" />
            </>
          )}
          <input required type="email" data-testid="auth-email" placeholder="E-posta" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full border rounded px-3 py-2 text-sm focus:border-[#1a1a1a] focus:outline-none" />
          <input required type="password" data-testid="auth-password" placeholder="Şifre (en az 6 karakter)" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full border rounded px-3 py-2 text-sm focus:border-[#1a1a1a] focus:outline-none" />
          <button type="submit" disabled={loading} data-testid="auth-submit" className="w-full bg-[#1a1a1a] hover:bg-[#333333] text-white py-2.5 rounded font-medium disabled:opacity-50">
            {loading ? '...' : mode === 'login' ? 'GİRİŞ YAP' : 'ÜYE OL'}
          </button>
          <button type="button" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setErr(''); }} className="w-full text-sm text-[#333333] hover:text-[#1a1a1a] py-1">
            {mode === 'login' ? 'Hesabınız yok mu? Üye Ol' : 'Zaten hesabınız var mı? Giriş Yap'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ================== Profile Modal ==================
const ProfileModal = ({ open, onClose }) => {
  const { user, logout } = useAuth();
  if (!open || !user) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="border-b px-5 py-3 flex items-center justify-between">
          <h3 className="font-serif font-bold text-lg text-[#2c1810]">👤 Profilim</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex flex-col items-center py-4">
            <div className="w-20 h-20 rounded-full bg-[#1a1a1a] text-white flex items-center justify-center text-3xl font-serif mb-2">
              {user.name?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="font-serif text-xl text-[#2c1810]">{user.name}</div>
          </div>
          <div className="space-y-2 text-sm border-t pt-3">
            <div className="flex justify-between"><span className="text-gray-500">E-posta:</span><span className="font-medium">{user.email}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Telefon:</span><span className="font-medium">{user.phone}</span></div>
          </div>
          <button onClick={() => { logout(); onClose(); }} data-testid="profile-logout" className="w-full mt-4 border border-red-500 text-red-500 hover:bg-red-50 py-2 rounded font-medium">
            ÇIKIŞ YAP
          </button>
        </div>
      </div>
    </div>
  );
};

// ================== Header ==================
const Header = () => {
  const { cartCount } = useCart();
  const settings = useSettings();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/categories`).then(res => setCategories(res.data));
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      {/* Top Banner */}
      <div className="bg-[#1a1a1a] text-white py-2 overflow-hidden text-sm">
        <div className="animate-scroll-left whitespace-nowrap">
          {Array(6).fill(0).map((_, i) => (
            <span key={i} className="inline-block px-8 font-medium">
              ⭐ {settings?.top_banner_text || '3 TAKIM ÜRÜN SADECE 2000 TL'} ⭐
            </span>
          ))}
        </div>
      </div>

      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="home-logo" className="flex items-center">
            <img
              src={settings?.logo_url}
              alt="Hasankeyf Tesettür"
              className="h-14 md:h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-[#2c1810] hover:text-[#1a1a1a] transition font-medium text-sm uppercase tracking-wider">Anasayfa</Link>
            <Link to="/kategori/yeniler" data-testid="nav-yeniler" className="text-[#1a1a1a] hover:text-[#333333] transition font-semibold text-sm uppercase tracking-wider">Yeniler</Link>
            {categories.map(cat => (
              <Link
                key={cat.id}
                to={`/kategori/${cat.slug}`}
                data-testid={`nav-category-${cat.slug}`}
                className="text-[#2c1810] hover:text-[#1a1a1a] transition font-medium text-sm uppercase tracking-wider"
              >
                {cat.name}
              </Link>
            ))}
            <Link to="/kategori/indirim" data-testid="nav-indirim" className="text-red-600 hover:text-red-700 transition font-semibold text-sm uppercase tracking-wider">İndirim</Link>
          </nav>

          {/* Right Icons */}
          <div className="flex items-center gap-3">
            {user ? (
              <button
                onClick={() => setProfileOpen(true)}
                data-testid="header-profile-button"
                className="hidden sm:flex items-center gap-2 text-sm text-[#2c1810] hover:text-[#1a1a1a] transition"
              >
                <div className="w-7 h-7 rounded-full bg-[#1a1a1a] text-white flex items-center justify-center text-xs font-serif">
                  {user.name?.[0]?.toUpperCase() || '?'}
                </div>
                <span className="hidden md:block font-medium">{user.name?.split(' ')[0]}</span>
              </button>
            ) : (
              <button
                onClick={() => setAuthOpen(true)}
                data-testid="header-login-button"
                className="hidden sm:block text-sm font-medium text-[#2c1810] hover:text-[#1a1a1a] transition"
              >
                👤 Giriş / Üye Ol
              </button>
            )}
            <Link to="/sepet" data-testid="header-cart-link" className="relative p-2 hover:text-[#1a1a1a] transition">
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <span data-testid="header-cart-count" className="absolute -top-1 -right-1 bg-[#1a1a1a] text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 font-bold">
                  {cartCount}
                </span>
              )}
            </Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-1" data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t pt-4 space-y-1">
            {user ? (
              <button onClick={() => { setMobileMenuOpen(false); setProfileOpen(true); }} className="block w-full text-left py-2 text-[#2c1810] font-medium">👤 Profilim ({user.name?.split(' ')[0]})</button>
            ) : (
              <button onClick={() => { setMobileMenuOpen(false); setAuthOpen(true); }} className="block w-full text-left py-2 text-[#2c1810] font-medium">👤 Giriş / Üye Ol</button>
            )}
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-[#2c1810] hover:text-[#1a1a1a] font-medium">Anasayfa</Link>
            <Link to="/kategori/yeniler" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-[#1a1a1a] font-semibold">✨ Yeniler</Link>
            {categories.map(cat => (
              <Link
                key={cat.id}
                to={`/kategori/${cat.slug}`}
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 text-[#2c1810] hover:text-[#1a1a1a]"
              >
                {cat.name}
              </Link>
            ))}
            <Link to="/kategori/indirim" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-red-600 font-semibold">🔥 İndirim</Link>
          </nav>
        )}
      </div>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
    </header>
  );
};

// ================== Product Card ==================
const ProductCard = ({ product }) => {
  return (
    <Link to={`/urun/${product.id}`} data-testid={`product-card-${product.id}`} className="group block">
      <div className="bg-white overflow-hidden hover:shadow-lg transition-shadow duration-300">
        <div className="relative aspect-[3/4] overflow-hidden bg-gray-50">
          <img
            src={product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          {product.discountPercent > 0 && (
            <div className="absolute top-3 left-3 bg-[#1a1a1a] text-white text-sm font-bold px-2 py-1 rounded">
              %{product.discountPercent}
            </div>
          )}
          {product.isNew && (
            <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
              Yeni
            </div>
          )}
        </div>
        <div className="p-3 text-center">
          <h3 className="product-name text-sm md:text-base text-[#2c1810] mb-2 line-clamp-2 min-h-[36px] md:min-h-[44px] group-hover:text-[#1a1a1a] transition-colors">
            {product.name} <span className="text-gray-400 font-normal text-xs">Standart</span>
          </h3>
          <div className="flex flex-col items-center gap-1">
            <span className="text-[10px] text-[#1a1a1a] font-semibold uppercase tracking-widest">
              Sepette %{product.discountPercent} İndirim
            </span>
            <div className="flex items-center gap-2">
              <span className="text-gray-400 line-through text-xs">{product.oldPrice.toLocaleString('tr-TR')} TL</span>
              <span className="text-[#2c1810] font-bold text-base md:text-lg">{product.price.toLocaleString('tr-TR')} TL</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

// ================== Home Page ==================
const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const settings = useSettings();

  useEffect(() => {
    axios.get(`${API}/products`).then(res => setProducts(res.data));
    axios.get(`${API}/categories`).then(res => setCategories(res.data));
  }, []);

  const takimProducts = products.filter(p => p.category === 'takim').slice(0, 8);
  const elbiseProducts = products.filter(p => p.category === 'elbise').slice(0, 8);
  const altGiyimProducts = products.filter(p => p.category === 'alt-giyim').slice(0, 8);
  const ustGiyimProducts = products.filter(p => p.category === 'ust-giyim').slice(0, 8);
  const disGiyimProducts = products.filter(p => p.category === 'dis-giyim').slice(0, 8);
  const newProducts = products.filter(p => p.isNew).slice(0, 8);

  const sectionData = [
    { key: 'yeni', title: 'YENİ ÜRÜNLER', slug: 'yeniler', products: newProducts },
    { key: 'takim', title: 'TAKIM MODELLERİMİZ', slug: 'takim', products: takimProducts },
    { key: 'elbise', title: 'ELBİSE MODELLERİMİZ', slug: 'elbise', products: elbiseProducts },
    { key: 'ust', title: 'ÜST GİYİM', slug: 'ust-giyim', products: ustGiyimProducts },
    { key: 'alt', title: 'ALT GİYİM', slug: 'alt-giyim', products: altGiyimProducts },
    { key: 'dis', title: 'DIŞ GİYİM', slug: 'dis-giyim', products: disGiyimProducts },
  ];

  return (
    <div className="bg-white">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-br from-[#f5e9d3] via-[#f9f3e6] to-[#f5e9d3] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="inline-block mb-4 px-4 py-1 bg-[#1a1a1a] text-white text-xs uppercase tracking-widest rounded-full">
            Yeni Sezon
          </div>
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-[#2c1810] mb-4 tracking-tight animate-fade-up">
            Hasankeyf <em className="italic font-medium text-[#333333]">Tesettür</em>
          </h1>
          <p className="text-lg md:text-xl text-[#5a4a3a] mb-8 max-w-2xl mx-auto font-light animate-fade-up" style={{ animationDelay: '0.15s' }}>
            Şıklığın ve zarafetin buluştuğu adres. Yeni sezon koleksiyonu ile tanışın.
          </p>
          {/* 3 Big CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-stretch max-w-4xl mx-auto animate-fade-up" style={{ animationDelay: '0.3s' }}>
            <Link
              to="/kategori/elbise"
              data-testid="hero-collection-button"
              className="flex-1 flex items-center justify-center gap-2 whitespace-nowrap bg-[#1a1a1a] hover:bg-[#333333] text-white px-4 md:px-6 py-4 md:py-5 text-sm md:text-base font-semibold tracking-wide transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 rounded"
            >
              🛍️ KOLEKSİYONU KEŞFET
            </Link>
            <a
              href={`https://wa.me/${settings?.whatsapp_number || '905016796642'}?text=${encodeURIComponent('Merhaba, Hasankeyf Tesettür\'den bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-button"
              className="flex-1 flex items-center justify-center gap-2 whitespace-nowrap bg-[#25D366] hover:bg-[#128C7E] text-white px-4 md:px-6 py-4 md:py-5 text-sm md:text-base font-semibold tracking-wide transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 rounded"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785A9.735 9.735 0 0 1 7.02 20.4l-.36-.214-3.75.982.999-3.648-.235-.374a9.735 9.735 0 0 1-1.512-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884M20.463 3.488A11.815 11.815 0 0 0 12.05.011C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
              </svg>
              WHATSAPP HATTI
            </a>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('openLiveChat'))}
              data-testid="hero-livechat-button"
              className="flex-1 flex items-center justify-center gap-2 whitespace-nowrap bg-white hover:bg-[#faf7f2] border-2 border-[#1a1a1a] text-[#1a1a1a] px-4 md:px-6 py-4 md:py-5 text-sm md:text-base font-semibold tracking-wide transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 rounded"
            >
              💬 CANLI DESTEK
            </button>
          </div>
        </div>
      </div>

      {/* Category Sections */}
      {sectionData.filter(s => s.products.length > 0).map(section => (
        <section key={section.key} className="max-w-7xl mx-auto px-4 py-12 md:py-16">
          <div className="text-center mb-10">
            <p className="text-[11px] text-[#1a1a1a] tracking-[0.3em] uppercase mb-2 font-medium">Koleksiyon</p>
            <h2 data-testid={`section-${section.key}-title`} className="text-3xl md:text-5xl font-serif font-semibold text-[#2c1810] mb-3 tracking-tight">
              {section.title}
            </h2>
            <div className="w-16 h-px bg-[#1a1a1a] mx-auto"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
            {section.products.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to={`/kategori/${section.slug}`}
              data-testid={`section-${section.key}-more`}
              className="inline-block px-8 py-2.5 border border-[#1a1a1a] text-[#333333] text-xs font-medium tracking-[0.2em] uppercase hover:bg-[#1a1a1a] hover:text-white transition-all duration-300"
            >
              Tümünü Gör
            </Link>
          </div>
        </section>
      ))}

      {/* Category Banners */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-4xl font-serif font-bold text-[#2c1810] mb-2">KATEGORİLER</h2>
          <div className="w-20 h-0.5 bg-[#1a1a1a] mx-auto"></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {categories.map(cat => (
            <Link
              key={cat.id}
              to={`/kategori/${cat.slug}`}
              data-testid={`category-banner-${cat.slug}`}
              className="relative overflow-hidden group aspect-[4/3] bg-gradient-to-br from-[#f5e9d3] to-[#e8d5b0] flex items-center justify-center"
            >
              <div className="text-center z-10 relative">
                <h3 className="text-xl md:text-3xl font-serif font-bold text-[#2c1810] mb-2">{cat.name}</h3>
                <p className="text-[#5a4a3a] italic mb-3 text-sm">Daha Fazlasını Keşfet</p>
                <span className="inline-block px-4 py-1.5 bg-[#2c1810] text-white text-xs tracking-widest group-hover:bg-[#1a1a1a] transition-colors">
                  KEŞFET
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-[#faf7f2] py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: '🛡️', title: 'Güvenli Alışveriş' },
              { icon: '🚚', title: 'Ücretsiz Kargo' },
              { icon: '💳', title: 'Havale/EFT İmkanı' },
              { icon: '✓', title: 'Kaliteli Ürünler' }
            ].map((f, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="text-4xl mb-3">{f.icon}</div>
                <h4 className="font-medium text-[#2c1810]">{f.title}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// ================== Product Detail Page ==================
const ProductDetailPage = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/products/${id}`).then(res => {
      setProduct(res.data);
      setSelectedSize(res.data.sizes[0]);
      setSelectedImage(0);
    });
  }, [id]);

  const handleAddToCart = () => {
    addToCart(product, selectedSize);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2500);
  };

  const handleBuyNow = () => {
    addToCart(product, selectedSize);
    navigate('/sepet');
  };

  if (!product) return <div className="min-h-[50vh] flex items-center justify-center text-[#1a1a1a]">Yükleniyor...</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {showSuccess && (
        <div data-testid="add-to-cart-success" className="fixed top-24 right-4 bg-green-600 text-white px-6 py-3 rounded shadow-lg z-50 animate-slide-in flex items-center gap-2">
          <Check className="w-5 h-5" /> Ürün sepete eklendi!
        </div>
      )}

      {/* Breadcrumb */}
      <div className="text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-[#1a1a1a]">Anasayfa</Link>
        <span className="mx-2">/</span>
        <Link to={`/kategori/${product.category}`} className="hover:text-[#1a1a1a] capitalize">{product.category}</Link>
        <span className="mx-2">/</span>
        <span className="text-[#2c1810]">{product.name}</span>
      </div>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        {/* Images */}
        <div>
          <div className="aspect-[3/4] bg-gray-50 overflow-hidden mb-4">
            <img src={product.images[selectedImage]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {product.images.length > 1 && (
            <div className="grid grid-cols-4 gap-2">
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`aspect-square overflow-hidden border-2 ${selectedImage === idx ? 'border-[#1a1a1a]' : 'border-gray-200'}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <div className="inline-block px-3 py-1 bg-[#f5e9d3] text-[#333333] text-xs uppercase tracking-widest mb-3">
            {product.category.replace('-', ' ')}
          </div>
          <h1 data-testid="product-detail-name" className="text-3xl md:text-4xl font-serif font-bold text-[#2c1810] mb-2">{product.name}</h1>
          <p className="text-sm text-gray-500 mb-6">Renk: <span className="text-[#2c1810] font-medium">{product.color}</span></p>

          {/* Price */}
          <div className="border-y border-gray-200 py-4 mb-6">
            <div className="flex items-baseline gap-3 mb-1">
              <span className="text-gray-400 line-through text-lg">{product.oldPrice.toLocaleString('tr-TR')} TL</span>
              <span data-testid="product-detail-price" className="text-3xl font-bold text-[#1a1a1a]">{product.price.toLocaleString('tr-TR')} TL</span>
              <span className="bg-[#1a1a1a] text-white text-xs font-bold px-2 py-1 rounded">%{product.discountPercent}</span>
            </div>
            <p className="text-sm text-green-700 font-medium">✓ Sepette %{product.discountPercent} İndirim</p>
          </div>

          <p className="text-gray-600 mb-6 leading-relaxed">{product.description}</p>

          {/* Size */}
          <div className="mb-6">
            <h3 className="font-semibold mb-3 text-sm text-[#2c1810]">Beden:</h3>
            <div className="flex flex-wrap gap-2">
              {product.sizes.map(size => (
                <button
                  key={size}
                  onClick={() => setSelectedSize(size)}
                  data-testid={`size-option-${size}`}
                  className={`min-w-[70px] px-4 py-2 border-2 text-sm font-medium transition ${
                    selectedSize === size
                      ? 'border-[#1a1a1a] bg-[#1a1a1a] text-white'
                      : 'border-gray-200 hover:border-[#1a1a1a]'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-3 mb-6">
            <button
              onClick={handleAddToCart}
              data-testid="add-to-cart-button"
              className="w-full bg-white border-2 border-[#2c1810] text-[#2c1810] py-3 font-medium tracking-wide hover:bg-[#2c1810] hover:text-white transition-colors"
            >
              SEPETE EKLE
            </button>
            <button
              onClick={handleBuyNow}
              data-testid="buy-now-button"
              className="w-full bg-[#1a1a1a] hover:bg-[#333333] text-white py-3 font-medium tracking-wide transition-colors"
            >
              HEMEN AL
            </button>
          </div>

          <div className="text-sm space-y-2 text-gray-600 border-t pt-4">
            <p>✓ Ücretsiz Kargo</p>
            <p>✓ Havale/EFT ile Ödeme</p>
            <p>✓ 1-3 iş günü içinde kargoda</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ================== Category Page ==================
const CategoryPage = () => {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);

  const SPECIAL_TITLES = {
    'yeniler': { name: 'Yeni Ürünler', desc: 'En yeni koleksiyonlarımız' },
    'indirim': { name: 'İndirimli Ürünler', desc: 'Kaçırılmayacak fırsatlar' },
    'cok-satanlar': { name: 'Çok Satanlar', desc: 'En çok tercih edilen ürünler' },
  };

  useEffect(() => {
    axios.get(`${API}/products/category/${slug}`).then(res => setProducts(res.data));
    if (SPECIAL_TITLES[slug]) {
      setCategory(SPECIAL_TITLES[slug]);
    } else {
      axios.get(`${API}/categories`).then(res => {
        setCategory(res.data.find(c => c.slug === slug));
      });
    }
  }, [slug]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="text-center mb-10">
        <h1 data-testid="category-page-title" className="text-3xl md:text-4xl font-serif font-bold text-[#2c1810] mb-2">
          {category?.name?.toUpperCase()}
        </h1>
        {category?.desc && <p className="text-sm text-gray-500 mb-3">{category.desc}</p>}
        <div className="w-20 h-0.5 bg-[#1a1a1a] mx-auto"></div>
        <p className="text-sm text-gray-500 mt-3">{products.length} ürün bulundu</p>
      </div>

      {products.length === 0 ? (
        <p className="text-center text-gray-500 py-12">Bu kategoride henüz ürün bulunmamaktadır.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
          {products.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </div>
  );
};

// ================== Cart Page ==================
const CartPage = () => {
  const { cart, updateQuantity, removeFromCart, subtotal } = useCart();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <ShoppingCart className="w-20 h-20 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-serif font-bold text-[#2c1810] mb-2">Sepetiniz Boş</h2>
          <p className="text-gray-600 mb-6">Alışverişe başlamak için ürünlerimizi inceleyin</p>
          <Link to="/" data-testid="empty-cart-shop-button" className="inline-block bg-[#1a1a1a] hover:bg-[#333333] text-white px-8 py-3 font-medium tracking-wide transition">
            ALIŞVERİŞE BAŞLA
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-serif font-bold text-[#2c1810] mb-8 text-center">Alışveriş Sepetim</h1>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Items */}
        <div className="lg:col-span-2 bg-white border border-gray-200">
          <div className="p-4 bg-[#faf7f2] border-b font-semibold text-[#2c1810]">
            Sepetinizdeki Ürünler ({cart.length})
          </div>
          {cart.map(item => (
            <div key={item.cartId} data-testid={`cart-item-${item.id}`} className="flex gap-4 p-4 border-b last:border-b-0">
              <img src={item.images[0]} alt={item.name} className="w-24 h-32 object-cover" />
              <div className="flex-1">
                <h3 className="font-medium text-[#2c1810] mb-1">{item.name}</h3>
                <p className="text-sm text-gray-500 mb-1">Renk: {item.color}</p>
                <p className="text-sm text-gray-500 mb-3">Beden: {item.size}</p>
                <p className="text-[#1a1a1a] font-bold">{item.price.toLocaleString('tr-TR')} TL</p>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button onClick={() => removeFromCart(item.cartId)} data-testid={`remove-item-${item.id}`} className="text-red-500 hover:text-red-700 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
                <div className="flex items-center border">
                  <button onClick={() => updateQuantity(item.cartId, item.quantity - 1)} className="p-2 hover:bg-gray-100">
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="px-3 font-semibold text-sm min-w-[40px] text-center">{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.cartId, item.quantity + 1)} className="p-2 hover:bg-gray-100">
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="bg-[#faf7f2] p-6 h-fit sticky top-32">
          <h3 className="font-serif font-bold text-xl text-[#2c1810] mb-4">Sipariş Özeti</h3>
          <div className="space-y-2 mb-4 pb-4 border-b border-gray-300">
            <div className="flex justify-between text-sm">
              <span>Ara Toplam</span>
              <span>{subtotal.toLocaleString('tr-TR')} TL</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Kargo</span>
              <span className="text-green-600 font-medium">Ücretsiz</span>
            </div>
          </div>
          <div className="flex justify-between mb-6">
            <span className="font-bold text-lg text-[#2c1810]">Toplam</span>
            <span data-testid="cart-total" className="font-bold text-2xl text-[#1a1a1a]">{subtotal.toLocaleString('tr-TR')} TL</span>
          </div>

          <button
            onClick={() => navigate('/odeme')}
            data-testid="checkout-button"
            className="w-full bg-[#1a1a1a] hover:bg-[#333333] text-white py-3 font-medium tracking-wide transition-colors mb-3"
          >
            SİPARİŞİ TAMAMLA
          </button>
          <Link to="/" data-testid="continue-shopping-link" className="block text-center text-sm text-[#333333] hover:text-[#1a1a1a]">
            Alışverişe Devam Et
          </Link>
        </div>
      </div>
    </div>
  );
};

// ================== Checkout Page (Havale/EFT) ==================
const CheckoutPage = () => {
  const { cart, subtotal, clearCart } = useCart();
  const settings = useSettings();
  const [step, setStep] = useState(1); // 1: form, 2: payment info
  const [copiedIban, setCopiedIban] = useState(null);
  const [orderInfo, setOrderInfo] = useState({
    name: '', phone: '', email: '', address: '', city: '', note: ''
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [paidChecked, setPaidChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderId] = useState(() => `HK-${Date.now().toString().slice(-8)}`);
  const navigate = useNavigate();

  useEffect(() => {
    if (cart.length === 0 && step === 1) {
      navigate('/');
    }
  }, [cart, step, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orderInfo.name || !orderInfo.phone || !orderInfo.address || !orderInfo.city) {
      alert('Lütfen zorunlu alanları doldurunuz.');
      return;
    }
    // IBAN yoksa direkt WhatsApp'a yönlendir (Havale/EFT adımını atla)
    if (!settings?.has_real_iban) {
      await submitOrderToWhatsApp();
      return;
    }
    setStep(2);
    window.scrollTo(0, 0);
  };

  const submitOrderToWhatsApp = async () => {
    setSubmitting(true);
    try {
      // Sipariş bilgilerini backend'e gönder (Telegram bildirimi de yapılır)
      const orderPayload = {
        order_id: orderId,
        name: orderInfo.name,
        phone: orderInfo.phone,
        email: orderInfo.email,
        address: orderInfo.address,
        city: orderInfo.city,
        note: orderInfo.note,
        items: cart.map(item => ({
          name: item.name, color: item.color, size: item.size,
          quantity: item.quantity, price: item.price,
        })),
        total: subtotal,
      };
      const formData = new FormData();
      formData.append('order_json', JSON.stringify(orderPayload));
      try {
        await axios.post(`${API}/orders/submit`, formData);
      } catch (e) { /* devam et */ }

      // WhatsApp mesajı - siparişin tüm detaylarıyla
      const message = `Merhaba, sipariş vermek istiyorum.\n\n` +
        `📋 *Sipariş No:* ${orderId}\n\n` +
        `👤 *Ad Soyad:* ${orderInfo.name}\n` +
        `📞 *Telefon:* ${orderInfo.phone}\n` +
        (orderInfo.email ? `✉️ *E-posta:* ${orderInfo.email}\n` : '') +
        `🏠 *Adres:* ${orderInfo.address}\n` +
        `🏙️ *İl/İlçe:* ${orderInfo.city}\n` +
        (orderInfo.note ? `📝 *Not:* ${orderInfo.note}\n` : '') +
        `\n📦 *Ürünler:*\n` +
        cart.map(item => `• ${item.name} (${item.color}, ${item.size}) x ${item.quantity} = ${(item.price * item.quantity).toLocaleString('tr-TR')} TL`).join('\n') +
        `\n\n💰 *Toplam: ${subtotal.toLocaleString('tr-TR')} TL*\n\n` +
        `Sipariş detaylarını onaylayıp bilgi vermenizi rica ederim. 🙏`;
      const url = `https://wa.me/${settings?.whatsapp_number}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
      clearCart();
      navigate('/');
    } catch (err) {
      alert('Sipariş gönderilemedi: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const copyIban = (iban, idx) => {
    navigator.clipboard.writeText(iban.replace(/\s/g, ''));
    setCopiedIban(idx);
    setTimeout(() => setCopiedIban(null), 2000);
  };

  const submitOrderAndNotify = async () => {
    if (!receiptFile) {
      alert('Lütfen dekont fotoğrafını yükleyin.');
      return;
    }
    if (!paidChecked) {
      alert('Lütfen "Ödemeyi Yaptım" kutucuğunu işaretleyin.');
      return;
    }

    setSubmitting(true);
    try {
      // Prepare order payload
      const orderPayload = {
        order_id: orderId,
        name: orderInfo.name,
        phone: orderInfo.phone,
        email: orderInfo.email,
        address: orderInfo.address,
        city: orderInfo.city,
        note: orderInfo.note,
        items: cart.map(item => ({
          name: item.name,
          color: item.color,
          size: item.size,
          quantity: item.quantity,
          price: item.price,
        })),
        total: subtotal,
      };

      // Send to backend (Telegram bildirimi backend tarafından yapılır)
      const formData = new FormData();
      formData.append('order_json', JSON.stringify(orderPayload));
      formData.append('receipt', receiptFile);

      await axios.post(`${API}/orders/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // WhatsApp mesajı hazırla
      const message = `Merhaba, sipariş numaram: *${orderId}*\n\n` +
        `👤 ${orderInfo.name}\n` +
        `📞 ${orderInfo.phone}\n\n` +
        `📦 Ürünlerim:\n` +
        cart.map(item => `• ${item.name} (${item.color}, ${item.size}) x ${item.quantity} = ${(item.price * item.quantity).toLocaleString('tr-TR')} TL`).join('\n') +
        `\n\n💰 *Toplam: ${subtotal.toLocaleString('tr-TR')} TL*\n\n` +
        `✅ Havale/EFT ile ödemeyi yaptım. Dekont hazır.\n\n` +
        `Sipariş hakkında bilgi almak istiyorum.\n\nHasankeyf Tesettür`;
      const url = `https://wa.me/${settings?.whatsapp_number}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
      clearCart();
      navigate('/');
    } catch (err) {
      alert('Sipariş gönderilemedi. Lütfen tekrar deneyin: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (!settings) return <div className="min-h-[50vh] flex items-center justify-center">Yükleniyor...</div>;

  // STEP 2: Payment Info
  if (step === 2) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-serif font-bold text-[#2c1810] mb-2">Siparişiniz Alındı!</h1>
          <p className="text-gray-600">Sipariş numaranız: <span className="font-bold text-[#1a1a1a]">{orderId}</span></p>
        </div>

        {/* Payment Instructions */}
        <div className="bg-[#faf7f2] border-l-4 border-[#1a1a1a] p-6 mb-6">
          <h2 className="font-bold text-lg text-[#2c1810] mb-3">💳 Havale/EFT Ödeme Adımları</h2>
          <ol className="space-y-2 text-sm text-gray-700 list-decimal list-inside">
            <li>Aşağıdaki banka hesaplarımızdan birine <b>{subtotal.toLocaleString('tr-TR')} TL</b> ödemenizi yapın.</li>
            <li>Açıklama kısmına sipariş numaranızı yazın: <b>{orderId}</b></li>
            <li><b>Dekont fotoğrafınızı</b> aşağıdan yükleyin.</li>
            <li><b>&quot;Ödemeyi Yaptım&quot;</b> kutucuğunu işaretleyin.</li>
            <li><b>WhatsApp&apos;a Yönlendir</b> butonuna basın, sipariş detaylarını iletin.</li>
          </ol>
        </div>

        {/* Bank Accounts */}
        <div className="space-y-4 mb-6">
          {settings.bank_accounts.map((account, idx) => (
            <div key={idx} data-testid={`bank-account-${idx}`} className="bg-white border border-gray-200 rounded p-5 hover:shadow-md transition">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-lg text-[#2c1810]">{account.bank_name}</h3>
                <span className="text-xs bg-[#1a1a1a] text-white px-2 py-1 rounded">HESAP {idx + 1}</span>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-500">Hesap Sahibi:</span>{' '}
                  <span className="font-medium text-[#2c1810]">{account.account_holder}</span>
                </div>
                <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
                  <div>
                    <span className="text-gray-500 text-xs">IBAN:</span>
                    <div className="font-mono font-bold text-[#2c1810]">{account.iban}</div>
                  </div>
                  <button
                    onClick={() => copyIban(account.iban, idx)}
                    data-testid={`copy-iban-${idx}`}
                    className="p-2 hover:bg-white rounded transition"
                    title="Kopyala"
                  >
                    {copiedIban === idx ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-[#1a1a1a]" />}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Receipt Upload */}
        <div className="bg-white border-2 border-dashed border-[#c9a961] rounded p-6 mb-4">
          <label htmlFor="receipt-upload" className="block cursor-pointer">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-[#faf7f2] rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-[#1a1a1a]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.9A5 5 0 0115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <h3 className="font-bold text-[#2c1810] mb-1">📸 Dekont Fotoğrafı Yükle *</h3>
              <p className="text-sm text-gray-500 mb-3">Ödemenizin dekontunu yükleyiniz (JPG, PNG, PDF - max 8MB)</p>
              {receiptFile ? (
                <div data-testid="receipt-uploaded-info" className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded text-sm">
                  <Check className="w-4 h-4" /> {receiptFile.name} ({(receiptFile.size / 1024).toFixed(0)} KB)
                </div>
              ) : (
                <span className="inline-block bg-[#1a1a1a] text-white px-6 py-2 rounded text-sm hover:bg-[#333333]">
                  Dosya Seç
                </span>
              )}
            </div>
            <input
              id="receipt-upload"
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
              data-testid="receipt-file-input"
            />
          </label>
        </div>

        {/* "Ödemeyi Yaptım" Checkbox */}
        <label className="flex items-start gap-3 bg-white border border-gray-200 rounded p-4 mb-6 cursor-pointer hover:border-[#1a1a1a] transition">
          <input
            type="checkbox"
            checked={paidChecked}
            onChange={(e) => setPaidChecked(e.target.checked)}
            data-testid="paid-checkbox"
            className="mt-1 w-5 h-5 accent-[#1a1a1a] cursor-pointer"
          />
          <span className="text-sm text-[#2c1810]">
            <b>Ödememi Yaptım.</b> {subtotal.toLocaleString('tr-TR')} TL tutarındaki ödemeyi yukarıdaki hesaplardan birine gönderdiğimi ve dekontu yüklediğimi onaylıyorum.
          </span>
        </label>

        {/* Order Summary */}
        <div className="bg-white border border-gray-200 rounded p-5 mb-6">
          <h3 className="font-bold text-[#2c1810] mb-3">Sipariş Özeti</h3>
          <div className="space-y-2 mb-3">
            {cart.map(item => (
              <div key={item.cartId} className="flex justify-between text-sm">
                <span className="text-gray-600">
                  {item.name} ({item.color}, {item.size}) x {item.quantity}
                </span>
                <span className="font-medium">{(item.price * item.quantity).toLocaleString('tr-TR')} TL</span>
              </div>
            ))}
          </div>
          <div className="pt-3 border-t flex justify-between font-bold text-lg">
            <span className="text-[#2c1810]">Toplam:</span>
            <span data-testid="checkout-total" className="text-[#1a1a1a]">{subtotal.toLocaleString('tr-TR')} TL</span>
          </div>
        </div>

        {/* WhatsApp Button - Only enabled when receipt uploaded AND checkbox checked */}
        <button
          onClick={submitOrderAndNotify}
          disabled={!receiptFile || !paidChecked || submitting}
          data-testid="whatsapp-notify-button"
          className={`w-full py-4 font-medium tracking-wide flex items-center justify-center gap-2 mb-3 transition-colors ${
            !receiptFile || !paidChecked || submitting
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          {submitting ? (
            <>Gönderiliyor...</>
          ) : (
            <>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785A9.735 9.735 0 0 1 7.02 20.4l-.36-.214-3.75.982.999-3.648-.235-.374a9.735 9.735 0 0 1-1.512-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884M20.463 3.488A11.815 11.815 0 0 0 12.05.011C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
              </svg>
              WhatsApp&apos;a Yönlendir ve Sipariş Bilgisi İstek
            </>
          )}
        </button>
        {(!receiptFile || !paidChecked) && (
          <p className="text-center text-xs text-red-500 mb-3">
            {!receiptFile && '⚠️ Dekont yüklenmedi'}
            {!receiptFile && !paidChecked && ' • '}
            {!paidChecked && '⚠️ "Ödemeyi Yaptım" işaretlenmedi'}
          </p>
        )}
        <Link to="/" data-testid="checkout-home-link" className="block text-center text-sm text-[#333333] hover:text-[#1a1a1a]">
          Anasayfaya Dön
        </Link>
      </div>
    );
  }

  // STEP 1: Address Form
  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-serif font-bold text-[#2c1810] mb-8 text-center">Sipariş Bilgileri</h1>

      <form onSubmit={handleSubmit} className="bg-white border border-gray-200 p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#2c1810] mb-1">Ad Soyad *</label>
            <input
              type="text"
              required
              value={orderInfo.name}
              onChange={e => setOrderInfo({ ...orderInfo, name: e.target.value })}
              data-testid="checkout-input-name"
              className="w-full border border-gray-300 px-3 py-2 focus:border-[#1a1a1a] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#2c1810] mb-1">Telefon *</label>
            <input
              type="tel"
              required
              value={orderInfo.phone}
              onChange={e => setOrderInfo({ ...orderInfo, phone: e.target.value })}
              data-testid="checkout-input-phone"
              placeholder="05XX XXX XX XX"
              className="w-full border border-gray-300 px-3 py-2 focus:border-[#1a1a1a] focus:outline-none"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#2c1810] mb-1">E-posta</label>
          <input
            type="email"
            value={orderInfo.email}
            onChange={e => setOrderInfo({ ...orderInfo, email: e.target.value })}
            data-testid="checkout-input-email"
            className="w-full border border-gray-300 px-3 py-2 focus:border-[#1a1a1a] focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#2c1810] mb-1">Adres *</label>
          <textarea
            required
            rows={3}
            value={orderInfo.address}
            onChange={e => setOrderInfo({ ...orderInfo, address: e.target.value })}
            data-testid="checkout-input-address"
            className="w-full border border-gray-300 px-3 py-2 focus:border-[#1a1a1a] focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#2c1810] mb-1">İl/İlçe *</label>
          <input
            type="text"
            required
            value={orderInfo.city}
            onChange={e => setOrderInfo({ ...orderInfo, city: e.target.value })}
            data-testid="checkout-input-city"
            className="w-full border border-gray-300 px-3 py-2 focus:border-[#1a1a1a] focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#2c1810] mb-1">Sipariş Notu</label>
          <textarea
            rows={2}
            value={orderInfo.note}
            onChange={e => setOrderInfo({ ...orderInfo, note: e.target.value })}
            data-testid="checkout-input-note"
            className="w-full border border-gray-300 px-3 py-2 focus:border-[#1a1a1a] focus:outline-none"
          />
        </div>

        <div className="border-t pt-4 mt-4">
          <div className="flex justify-between mb-4">
            <span className="font-medium text-[#2c1810]">Toplam:</span>
            <span className="font-bold text-2xl text-[#1a1a1a]">{subtotal.toLocaleString('tr-TR')} TL</span>
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="checkout-submit-button"
            className="w-full bg-[#1a1a1a] hover:bg-[#333333] text-white py-3 font-medium tracking-wide transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? 'GÖNDERİLİYOR...' : settings?.has_real_iban ? 'HAVALE/EFT İLE DEVAM' : (
              <>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785A9.735 9.735 0 0 1 7.02 20.4l-.36-.214-3.75.982.999-3.648-.235-.374a9.735 9.735 0 0 1-1.512-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884M20.463 3.488A11.815 11.815 0 0 0 12.05.011C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
                </svg>
                İLERİ - WHATSAPP'A GÖNDER
              </>
            )}
          </button>
          {!settings?.has_real_iban && (
            <p className="text-xs text-gray-500 text-center mt-2">
              Sipariş bilgileriniz WhatsApp'ta bize iletilecek.
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

// ================== Footer ==================
const Footer = () => {
  const settings = useSettings();
  return (
    <footer className="bg-[#2c1810] text-white mt-16">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <h3 className="font-serif font-bold text-xl mb-4">Hasankeyf Tesettür</h3>
            <p className="text-gray-300 text-sm">Zariflik ve şıklığın buluştuğu adres. Kaliteli tesettür giyim ürünleri.</p>
          </div>
          <div>
            <h3 className="font-bold text-sm uppercase tracking-widest mb-4 text-[#1a1a1a]">Kurumsal</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>Hakkımızda</li>
              <li>İletişim</li>
              <li>Kargo & Teslimat</li>
              <li>İade & Değişim</li>
            </ul>
          </div>
          <div>
            <h3 className="font-bold text-sm uppercase tracking-widest mb-4 text-[#1a1a1a]">Kategoriler</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li><Link to="/kategori/takim" className="hover:text-[#1a1a1a]">Takım</Link></li>
              <li><Link to="/kategori/elbise" className="hover:text-[#1a1a1a]">Elbise</Link></li>
              <li><Link to="/kategori/alt-giyim" className="hover:text-[#1a1a1a]">Alt Giyim</Link></li>
              <li><Link to="/kategori/ust-giyim" className="hover:text-[#1a1a1a]">Üst Giyim</Link></li>
              <li><Link to="/kategori/dis-giyim" className="hover:text-[#1a1a1a]">Dış Giyim</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-bold text-sm uppercase tracking-widest mb-4 text-[#1a1a1a]">İletişim</h3>
            <div className="space-y-2 text-sm text-gray-300">
              <p>📱 WhatsApp: {settings?.whatsapp_display}</p>
              <p>📍 {settings?.address}</p>
              <p>💳 Havale/EFT</p>
              <p>🚚 Ücretsiz Kargo</p>
              <a
                href={`https://instagram.com/${settings?.instagram}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 hover:text-[#1a1a1a] mt-2"
              >
                <Instagram className="w-4 h-4" /> @{settings?.instagram}
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-700 pt-6 text-center text-sm text-gray-400">
          © 2026 Hasankeyf Tesettür. Tüm hakları saklıdır.
        </div>
      </div>
    </footer>
  );
};

// ================== Mobile Bottom Bar ==================
const MobileBottomBar = () => {
  const { cartCount } = useCart();
  const settings = useSettings();
  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t z-40 shadow-lg">
      <div className="grid grid-cols-3">
        <Link to="/" data-testid="mobile-nav-home" className="flex flex-col items-center py-2 text-[#2c1810] hover:text-[#1a1a1a]">
          <HomeIcon className="w-5 h-5" />
          <span className="text-xs mt-1">Anasayfa</span>
        </Link>
        <Link to="/sepet" data-testid="mobile-nav-cart" className="flex flex-col items-center py-2 text-[#2c1810] hover:text-[#1a1a1a] relative">
          <ShoppingCart className="w-5 h-5" />
          {cartCount > 0 && (
            <span className="absolute top-1 right-8 bg-[#1a1a1a] text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
              {cartCount}
            </span>
          )}
          <span className="text-xs mt-1">Sepetim</span>
        </Link>
        <a
          href={`https://wa.me/${settings?.whatsapp_number}?text=Merhaba,%20iletişime%20geçmek%20istiyorum.`}
          target="_blank"
          rel="noreferrer"
          data-testid="mobile-nav-whatsapp"
          className="flex flex-col items-center py-2 text-green-600"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785A9.735 9.735 0 0 1 7.02 20.4l-.36-.214-3.75.982.999-3.648-.235-.374a9.735 9.735 0 0 1-1.512-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884M20.463 3.488A11.815 11.815 0 0 0 12.05.011C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
          </svg>
          <span className="text-xs mt-1">WhatsApp</span>
        </a>
      </div>
    </div>
  );
};

// ================== Main App ==================
function StoreApp() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <CartProvider>
          <div className="min-h-screen flex flex-col bg-white pb-16 md:pb-0">
            <Header />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/urun/:id" element={<ProductDetailPage />} />
                <Route path="/kategori/:slug" element={<CategoryPage />} />
                <Route path="/sepet" element={<CartPage />} />
                <Route path="/odeme" element={<CheckoutPage />} />
              </Routes>
            </main>
            <Footer />
            <MobileBottomBar />
            <LiveChat />
          </div>
        </CartProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/*" element={<StoreApp />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
